///
/// EarthBrowser
///
/// Created by Matt Giger on 1/18/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export interface PressureCenterFeature extends DatasetFeature {
	id: string
	valid_time: string
	center_type: string
	lon: number
	lat: number
	pressure_hpa: number
	track_id: string
	track_seq: number
}

export interface PressureFieldTextureFeature extends DatasetFeature {
	id: string
	valid_time: string
	image_url: string
	min_pressure_hpa: number
	max_pressure_hpa: number
	min_lon: number
	max_lon: number
	min_lat: number
	max_lat: number
	pressure_offset_hpa: number
	pressure_scale_inv: number
}

/** Computed pressure field blobs persisted so purged GPU frames can be
 *  re-fetched on demand instead of retaining CPU copies in heap.
 *  field: int16 quantized hPa (see SynopticFieldCodec), 0 = invalid
 *  flow: float32 interleaved RG displacement in UV units, nullable */
export interface PressureFieldBlobRow {
	id: string
	valid_time: string
	width: number
	height: number
	field: Uint8Array
	flow: Uint8Array | null
	flow_width: number
	flow_height: number
}

/** Packed RGBA8 jet stream wind frames (see JetStreamFieldGenerator),
 *  persisted for the same reason as the pressure fields */
export interface JetFieldBlobRow {
	id: string
	valid_time: string
	width: number
	height: number
	max_speed_mps: number
	field: Uint8Array
}

const cBatchSize = 500

export class SynopticTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists synoptic_pressure_centers (
					id text primary key,
					valid_time text not null,
					center_type text not null,
					lon real not null,
					lat real not null,
					pressure_hpa real not null,
					track_id text not null default '',
					track_seq integer not null default 0
				)`
			},
			{ sql: "create index if not exists idx_synoptic_pressure_centers_valid_time on synoptic_pressure_centers(valid_time)" },
			{ sql: "create index if not exists idx_synoptic_pressure_centers_track_id on synoptic_pressure_centers(track_id)" },
			{
				sql: `create table if not exists synoptic_pressure_fields (
					id text primary key,
					valid_time text not null,
					width integer not null,
					height integer not null,
					field blob not null,
					flow blob,
					flow_width integer not null default 0,
					flow_height integer not null default 0
				)`
			},
			{
				sql: `create table if not exists synoptic_jet_fields (
					id text primary key,
					valid_time text not null,
					width integer not null,
					height integer not null,
					max_speed_mps real not null default 0,
					field blob not null
				)`
			},
		])
	}

	public static async clearJetFields(): Promise<void> {
		await Database.db.exec("delete from synoptic_jet_fields", [], "veryLow")
	}

	public static async insertJetField(row: JetFieldBlobRow): Promise<void> {
		await Database.db.exec(
			`insert or replace into synoptic_jet_fields (
				id, valid_time, width, height, max_speed_mps, field
			) values (?, ?, ?, ?, ?, ?)`,
			[row.id, row.valid_time, row.width, row.height, row.max_speed_mps, row.field],
			"veryLow"
		)
	}

	public static async getJetField(id: string): Promise<JetFieldBlobRow | null> {
		const rows = await Database.db.exec(
			`select id, valid_time, width, height, max_speed_mps, field
			from synoptic_jet_fields
			where id = ?`,
			[id],
			"medium"
		) as JetFieldBlobRow[]
		return rows[0] ?? null
	}

	public static async clearPressureFields(): Promise<void> {
		await Database.db.exec("delete from synoptic_pressure_fields", [], "veryLow")
	}

	/** One ~2MB blob per call so interactive queries interleave between
	 *  inserts on the shared database queue */
	public static async insertPressureField(row: PressureFieldBlobRow): Promise<void> {
		await Database.db.exec(
			`insert or replace into synoptic_pressure_fields (
				id, valid_time, width, height, field, flow, flow_width, flow_height
			) values (?, ?, ?, ?, ?, ?, ?, ?)`,
			[row.id, row.valid_time, row.width, row.height, row.field, row.flow, row.flow_width, row.flow_height],
			"veryLow"
		)
	}

	/** Medium priority: these are demand faults on the scrub/animation path */
	public static async getPressureField(id: string): Promise<PressureFieldBlobRow | null> {
		const rows = await Database.db.exec(
			`select id, valid_time, width, height, field, flow, flow_width, flow_height
			from synoptic_pressure_fields
			where id = ?`,
			[id],
			"medium"
		) as PressureFieldBlobRow[]
		return rows[0] ?? null
	}

	public static async replaceAllPressureCenters(rows: PressureCenterFeature[]): Promise<void> {
		await Database.db.exec("delete from synoptic_pressure_centers", [], "critical")
		await this.upsertPressureCenters(rows)
	}

	public static async upsertPressureCenters(rows: PressureCenterFeature[]): Promise<void> {
		const statement = `insert into synoptic_pressure_centers (
				id, valid_time, center_type, lon, lat, pressure_hpa, track_id, track_seq
			) values (?, ?, ?, ?, ?, ?, ?, ?)
			on conflict(id) do update set
				valid_time = excluded.valid_time,
				center_type = excluded.center_type,
				lon = excluded.lon,
				lat = excluded.lat,
				pressure_hpa = excluded.pressure_hpa,
				track_id = excluded.track_id,
				track_seq = excluded.track_seq`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(r => [
				r.id,
				r.valid_time,
				r.center_type,
				r.lon,
				r.lat,
				r.pressure_hpa,
				r.track_id,
				r.track_seq,
			]))
		}
	}

	public static async getPressureCentersForTime(validTime: string): Promise<PressureCenterFeature[]> {
		const rows = await Database.db.exec(
			`select id, valid_time, center_type, lon, lat, pressure_hpa, track_id, track_seq
			from synoptic_pressure_centers
			where valid_time = ?`,
			[validTime],
			"low"
		) as PressureCenterFeature[]
		return rows
	}

	public static async getDistinctPressureCenterTimes(): Promise<string[]> {
		const rows = await Database.db.exec(
			`select distinct valid_time from synoptic_pressure_centers order by valid_time`,
			[],
			"low"
		) as { valid_time: string }[]
		return rows.map(r => r.valid_time)
	}
}
